import { useState } from 'react';
import {
  LOCKED_AFTER_ENROLLMENT,
  type LockedAfterEnrollment,
  type TenantView,
  type UpdateTenant,
} from '@teachpartner/contracts';
import { ApiError, describeError } from '../../api/client';
import { useServices } from '../../app-context';
import { ErrorBanner } from '../../components/badges';
import { TenantForm, toTenantFields, type TenantFormValues } from '../../components/TenantForm';

interface CanonicalWarning {
  readonly from: string;
  readonly to: string;
  readonly patch: UpdateTenant;
}

export function formValues(t: TenantView): TenantFormValues {
  return {
    code: t.code,
    name: t.name,
    shortName: t.shortName,
    bootstrapAdminEmail: t.bootstrapAdminEmail ?? '',
    customDomain: t.customDomain ?? '',
    exposureMode: t.exposureMode,
    tailscaleAddress: t.tailscaleAddress ?? '',
    status: t.status,
    plan: t.subscription.plan ?? '',
    startsOn: t.subscription.startsOn ?? '',
    endsOn: t.subscription.endsOn ?? '',
    contactName: t.subscription.contactName ?? '',
    contactEmail: t.subscription.contactEmail ?? '',
  };
}

/**
 * Fields read-only for this tenant: after enrollment the custom domain and exposure, and the
 * Tailscale address once it is set (owner decision 2026-10-10; central enforces it too).
 */
export function lockedFields(t: TenantView): LockedAfterEnrollment[] {
  if (!t.enrolledAt) return [];
  return LOCKED_AFTER_ENROLLMENT.filter((f) => f !== 'tailscaleAddress' || t.tailscaleAddress);
}

/** The first Tenant Admin is sent only before enrollment and only when it changed. */
function toPatch(t: TenantView, v: TenantFormValues): UpdateTenant {
  const email = v.bootstrapAdminEmail.trim();
  const changed = !t.enrolledAt && email !== (t.bootstrapAdminEmail ?? '');
  const locked = new Set<string>(lockedFields(t));
  const fields = Object.fromEntries(
    Object.entries(toTenantFields(v)).filter(([key]) => !locked.has(key)),
  ) as UpdateTenant;
  return { ...fields, ...(changed ? { bootstrapAdminEmail: email } : {}) };
}

function canonicalWarning(error: unknown, patch: UpdateTenant): CanonicalWarning | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const d = error.details as { warning?: string; from?: string; to?: string } | undefined;
  return d?.warning === 'CANONICAL_HOSTNAME_CHANGED' && d.from && d.to
    ? { from: d.from, to: d.to, patch }
    : null;
}

/** Edit tenant. A canonical-hostname change needs explicit confirmation (issuer changes). */
export function SettingsSection({ tenant, onSaved }: { tenant: TenantView; onSaved(): void }) {
  const { api } = useServices();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warning, setWarning] = useState<CanonicalWarning | null>(null);

  const save = async (patch: UpdateTenant) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.updateTenant(tenant.code, patch);
      setWarning(null);
      const sync = result.statusSync;
      setNotice(
        [
          'Saved.',
          ...result.warnings.map((w) => w.message),
          sync && !sync.ok ? `School not told about the status change: ${sync.error ?? ''}` : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
      onSaved();
    } catch (e) {
      const w = canonicalWarning(e, patch);
      if (w) setWarning(w);
      else setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Settings">
      <h2>Settings</h2>
      <ErrorBanner message={error} />
      {notice && <div className="banner banner-info">{notice}</div>}
      {warning && (
        <div
          className="banner banner-warn"
          role="alertdialog"
          aria-label="Canonical hostname change"
        >
          <p>
            This moves the canonical hostname from <strong>{warning.from}</strong> to{' '}
            <strong>{warning.to}</strong>. The school&apos;s Keycloak issuer and the
            Google/Microsoft redirect URIs change too; follow the runbook before confirming.
          </p>
          <button
            type="button"
            onClick={() => void save({ ...warning.patch, confirmCanonicalChange: true })}
          >
            Confirm hostname change
          </button>
          <button type="button" className="link" onClick={() => setWarning(null)}>
            Cancel
          </button>
        </div>
      )}
      <TenantForm
        key={tenant.code}
        initial={formValues(tenant)}
        mode="edit"
        busy={busy}
        showAdminEmail={!tenant.enrolledAt}
        locked={lockedFields(tenant)}
        onSubmit={(v) => void save(toPatch(tenant, v))}
      />
    </section>
  );
}
